import { useLocalization } from "@fluent/react";
import { Row, formatNumber } from "@narmi/design_system";
import {
  LIMITS_DATA,
  hasWritePermissionForModels,
} from "./OrganizationRoles.helpers";
import styles from "./OrganizationRoles.module.scss";

interface RoleLimitsViewProps {
  limits: API.Limits.ByType;
  permissions: API.Permission[];
  trailingDivider?: boolean;
}

export default function RoleLimitsView({
  limits,
  permissions,
  trailingDivider = false,
}: RoleLimitsViewProps) {
  const { l10n } = useLocalization();
  const rows = Object.entries(limits).flatMap(([limitType, limitValue]) => {
    const limitData = LIMITS_DATA[limitType];
    if (
      !limitData ||
      !hasWritePermissionForModels(permissions, limitData.modelStrs)
    ) {
      return [];
    }
    const periods = Object.entries(limitValue)
      .filter(([, amount]) => Number(amount) > 0)
      .sort(([a], [b]) => Number(a) - Number(b));
    if (periods.length === 0) return [];

    const secCodeLimits =
      limitType === "unverified_ach_push" ||
      limitType === "unverified_ach_pull"
        ? Object.entries(limits).find(
            ([key]) => key === `${limitType}_by_sec_code`
          )?.[1]
        : undefined;
    const secCodeRows = Object.entries(secCodeLimits ?? {})
      .filter(([code]) => ["PPD", "CCD", "WEB"].includes(code))
      .map(([code, savedPeriods]) => ({
        key: `${limitType}:${code}`,
        label: l10n.getString("org-role-limit-type", { limitKey: code }),
        nested: true,
        periods: Object.entries(savedPeriods ?? {})
          .filter(
            ([period, amount]) =>
              !Number.isNaN(Number(period)) && Number(amount) >= 0
          )
          .sort(([a], [b]) => Number(a) - Number(b)),
      }))
      .filter((row) => row.periods.length > 0);

    return [
      {
        key: limitType,
        label: l10n.getString(limitData.ftlId),
        nested: false,
        periods,
      },
      ...secCodeRows,
    ];
  });

  return (
    <>
      {rows.map(({ key, label, nested, periods }, index) => (
        <div key={key}>
          <div className={nested ? styles.nestedLimitCell : undefined}>
            <Row>
              <Row.Item>
                <p>{label}</p>
              </Row.Item>
              <Row.Item shrink>
                <div style={{ textAlign: "right" }}>
                  {nested &&
                  periods.some(
                    ([period, amount]) => period === "1" && Number(amount) === 0
                  ) ? (
                    <p>
                      <span className="narmi-icon-slash margin--right--xs" aria-hidden="true" />
                      {key.startsWith("unverified_ach_pull:")
                        ? l10n.getString("org-role-limits-sec-code-disabled-collect")
                        : l10n.getString("org-role-limits-sec-code-disabled-send")}
                    </p>
                  ) : periods.map(([period, amount]) => (
                    <p
                      key={period}
                      style={
                        period !== "1"
                          ? { color: "var(--color-mediumGrey)" }
                          : undefined
                      }
                    >
                      {formatNumber(Number(amount), "currency")}{" "}
                      {l10n.getString("org-role-limit-period-per-days", {
                        days: Number(period),
                      })}
                    </p>
                  ))}
                </div>
              </Row.Item>
            </Row>
          </div>
          {(trailingDivider || index < rows.length - 1) && (
            <hr className={styles.limitsdivider} />
          )}
        </div>
      ))}
    </>
  );
}
